import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {GraduationCap, LogOut, Plus, Calendar, MapPin, Trash2, Edit3, X, CheckCircle2, AlertCircle, Megaphone, ChevronDown, ChevronUp, Clock, XCircle, Lock, UserCircle, BookOpen, MessageSquare} from 'lucide-react';
import toast from "react-hot-toast";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import DashboardThemeWrapper from "@/components/DashboardThemeWrapper";
import { useAuth } from "@/hooks/useAuth";
import { useMessaging } from "@/hooks/useMessaging";
import { REGIONS, type Region } from "@/data/formations";
import CompanyProfileForm from "@/components/CompanyProfileForm";
import AccountSettings from "@/components/AccountSettings";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import {
  PENDING_FORMATION_DRAFT_KEY,
} from "@/hooks/useFormationDepositFlow";
import type { FormationDepositPayload } from "@/components/FormationFormModal";
import { lumi } from "@/lib/lumi";

type ValidationStatus = "pending" | "approved" | "rejected";

interface OrganismeFormationDoc {
  _id: string;
  title: string;
  category: "BNSSA" | "MNS" | "Recyclage" | "Perfectionnement";
  organisme?: string;
  location: string;
  region: Region;
  duration?: string;
  sessionDate: string; // ISO date
  description?: string;
  programme?: string;
  prerequisites?: string;
  certification?: string;
  contact?: string;
  creator: string;
  creatorEmail?: string;
  validationStatus?: ValidationStatus;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

const EMPTY_FORM = {
  title: "",
  category: "BNSSA" as OrganismeFormationDoc["category"],
  location: "",
  region: "Île-de-France" as Region,
  duration: "",
  sessionDate: "",
  description: "",
  programme: "",
  prerequisites: "",
  certification: "",
  contact: "",
};

function StatusPill({ status }: { status: ValidationStatus | undefined }) {
  const s = status ?? "pending";
  const map = {
    pending: { bg: "bg-amber-100", text: "text-amber-800", icon: Clock, label: "En attente de validation" },
    approved: { bg: "bg-emerald-100", text: "text-emerald-800", icon: CheckCircle2, label: "Publiée" },
    rejected: { bg: "bg-red-100", text: "text-red-800", icon: XCircle, label: "Refusée" },
  }[s];
  const Icon = map.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${map.bg} ${map.text}`}>
      <Icon className="w-3 h-3" />
      {map.label}
    </span>
  );
}

export default function DashboardOrganisme() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return t === "compte" ? "compte" : "formations";
  })();
  const [activeTab, setActiveTab] = useState<"formations" | "compte">(initialTab);

  const handleChangeTab = (tab: "formations" | "compte") => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "formations") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const { user, profile, isAuthenticated, isCandidate, isRecruiter, isAdmin, signOut } = useAuth();

  const creator = user?.userId || "";
  const email = user?.email || "";

  // Messagerie interne — nombre total de messages non lus pour afficher un
  // pastille rouge sur le bouton « Messagerie » du hero.
  const { conversations: messagingConversations } = useMessaging(creator);
  const unreadMessagesCount = useMemo(() => {
    if (!creator || !Array.isArray(messagingConversations)) return 0;
    return messagingConversations.reduce((sum, c: { unreadCounts?: Record<string, number> }) => {
      const n = c?.unreadCounts?.[creator] || 0;
      return sum + (typeof n === "number" ? n : 0);
    }, 0);
  }, [creator, messagingConversations]);

  // Coordonnées entreprise (obligatoires pour publier)
  const { profile: companyProfile, isComplete: isCompanyComplete, refresh: refreshCompany, upsert: upsertCompany } = useCompanyProfile(
    creator,
    email,
    "organisme"
  );

  const [coordsOpen, setCoordsOpen] = useState(true);

  const [items, setItems] = useState<OrganismeFormationDoc[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // ── Route protection ─────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/inscription?role=organisme", { replace: true });
    } else if (isAdmin) {
      // L'admin peut consulter n'importe quel espace : pas de redirection forcée.
    } else if (isCandidate) {
      navigate("/dashboard", { replace: true });
    } else if (isRecruiter) {
      navigate("/backoffice", { replace: true });
    }
  }, [isAuthenticated, isCandidate, isRecruiter, isAdmin, navigate]);

  // ── Charger les formations stockées en base ──────────────────────
  const fetchMyFormations = useCallback(async () => {
    if (!creator) return;
    setLoadingList(true);
    try {
      const { list } = await lumi.entities.organisme_formations.list({
        filter: { creator },
        sort: { createdAt: -1 },
        limit: 200,
      });
      setItems((list as OrganismeFormationDoc[]) || []);
    } catch (e) {
      console.error("[organisme_formations] fetch failed", e);
      toast.error("Impossible de charger vos formations.");
    } finally {
      setLoadingList(false);
    }
  }, [creator]);

  useEffect(() => {
    if (creator) fetchMyFormations();
  }, [creator, fetchMyFormations]);

  // ── Consommer un brouillon de formation déposé avant inscription ───
  // Lorsqu'un visiteur a rempli la pop-up « Annoncer une formation » sur
  // /formations sans être connecté, ses données ont été stockées en
  // sessionStorage. À la première arrivée de l'organisme sur son dashboard,
  // on enregistre ses coordonnées puis on crée la formation (en attente de
  // validation), avant de vider le brouillon. Le useRef évite tout double
  // traitement (StrictMode, re-render).
  const draftConsumedRef = useRef(false);
  useEffect(() => {
    if (draftConsumedRef.current) return;
    if (!isAuthenticated || !creator) return;
    // L'admin n'est pas un véritable organisme : on ne consomme pas pour lui.
    if (isCandidate || isRecruiter) return;

    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(PENDING_FORMATION_DRAFT_KEY);
    } catch {
      raw = null;
    }
    if (!raw) return;

    draftConsumedRef.current = true;

    const finalize = async () => {
      let data: FormationDepositPayload;
      try {
        data = JSON.parse(raw as string) as FormationDepositPayload;
      } catch {
        try { sessionStorage.removeItem(PENDING_FORMATION_DRAFT_KEY); } catch { /* noop */ }
        return;
      }
      if (!data?.company || !data?.formation) {
        try { sessionStorage.removeItem(PENDING_FORMATION_DRAFT_KEY); } catch { /* noop */ }
        return;
      }

      const now = new Date().toISOString();
      try {
        // 1) Enregistrer / compléter les coordonnées d'organisme
        await upsertCompany({
          raisonSociale: data.company.raisonSociale,
          siret: data.company.siret || "",
          adresse: data.company.adresse || "",
          telephone: data.company.telephone,
          responsableNom: data.company.responsableNom,
          responsableFonction: data.company.responsableFonction || "",
          logoUrl: companyProfile?.logoUrl || "",
        });

        // 2) Créer la formation (en attente de validation admin)
        const sessionIso =
          data.formation.sessionDate.length === 10
            ? `${data.formation.sessionDate}T00:00:00.000Z`
            : new Date(data.formation.sessionDate).toISOString();
        await lumi.entities.organisme_formations.create({
          title: data.formation.title,
          category: data.formation.category,
          organisme: data.company.raisonSociale,
          location: data.formation.location,
          region: data.formation.region,
          duration: data.formation.duration || "",
          sessionDate: sessionIso,
          description: data.formation.description || "",
          programme: data.formation.programme || "",
          prerequisites: data.formation.prerequisites || "",
          certification: data.formation.certification || "",
          contact: data.formation.contact || "",
          creator,
          creatorEmail: data.company.email || email,
          validationStatus: "pending" as ValidationStatus,
          createdAt: now,
          updatedAt: now,
        });

        try { sessionStorage.removeItem(PENDING_FORMATION_DRAFT_KEY); } catch { /* noop */ }
        await Promise.all([fetchMyFormations(), refreshCompany()]);
        toast.success("Votre formation a bien été soumise. Elle sera publiée après validation.");
      } catch (err) {
        console.error("[DashboardOrganisme] formation draft finalize failed", err);
        // En cas d'échec, on conserve le brouillon pour permettre une nouvelle tentative.
        draftConsumedRef.current = false;
        toast.error("Impossible de finaliser votre annonce de formation. Réessayez depuis « Publier une formation ».");
      }
    };

    finalize();
  }, [
    isAuthenticated,
    creator,
    email,
    isCandidate,
    isRecruiter,
    companyProfile,
    upsertCompany,
    refreshCompany,
    fetchMyFormations,
  ]);

  // Replier automatiquement le bloc coordonnées une fois complet
  useEffect(() => {
    if (isCompanyComplete) {
      setCoordsOpen(false);
    } else {
      setCoordsOpen(true);
    }
  }, [isCompanyComplete]);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Vous êtes déconnecté.");
    navigate("/", { replace: true });
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleStartCreate = () => {
    if (!isCompanyComplete) {
      toast.error("Merci de compléter d'abord vos coordonnées d'organisme.");
      setCoordsOpen(true);
      return;
    }
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!creator) return;
    if (!isCompanyComplete) {
      toast.error("Coordonnées d'organisme incomplètes — publication bloquée.");
      setCoordsOpen(true);
      return;
    }
    if (!form.title.trim() || !form.location.trim() || !form.sessionDate) {
      toast.error("Merci de renseigner les champs obligatoires.");
      return;
    }

    setSubmitting(true);
    const now = new Date().toISOString();
    try {
      if (editingId) {
        const patch = {
          title: form.title.trim(),
          category: form.category,
          organisme: companyProfile?.raisonSociale || profile?.company || "",
          location: form.location.trim(),
          region: form.region,
          duration: form.duration.trim(),
          sessionDate: form.sessionDate,
          description: form.description.trim(),
          programme: form.programme.trim(),
          prerequisites: form.prerequisites.trim(),
          certification: form.certification.trim(),
          contact: form.contact.trim(),
          updatedAt: now,
          // En cas d'édition après refus, on repasse en pending
          validationStatus: "pending" as ValidationStatus,
          rejectionReason: "",
        };
        await lumi.entities.organisme_formations.update(editingId, patch);
        toast.success("Formation mise à jour, en attente de revalidation.");
      } else {
        const sessionIso = form.sessionDate.length === 10
          ? `${form.sessionDate}T00:00:00.000Z`
          : new Date(form.sessionDate).toISOString();
        const doc = {
          title: form.title.trim(),
          category: form.category,
          organisme: companyProfile?.raisonSociale || profile?.company || "",
          location: form.location.trim(),
          region: form.region,
          duration: form.duration.trim(),
          sessionDate: sessionIso,
          description: form.description.trim(),
          programme: form.programme.trim(),
          prerequisites: form.prerequisites.trim(),
          certification: form.certification.trim(),
          contact: form.contact.trim(),
          creator,
          creatorEmail: email,
          validationStatus: "pending" as ValidationStatus,
          createdAt: now,
          updatedAt: now,
        };
        await lumi.entities.organisme_formations.create(doc);
        toast.success("Formation soumise. Elle sera publiée après validation par l'administrateur.");
      }
      await fetchMyFormations();
      resetForm();
    } catch (err) {
      console.error("[organisme_formations] save failed", err);
      toast.error("Impossible d'enregistrer la formation.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item: OrganismeFormationDoc) => {
    setEditingId(item._id);
    setForm({
      title: item.title,
      category: item.category,
      location: item.location,
      region: item.region,
      duration: item.duration || "",
      sessionDate: (item.sessionDate || "").slice(0, 10),
      description: item.description || "",
      programme: item.programme || "",
      prerequisites: item.prerequisites || "",
      certification: item.certification || "",
      contact: item.contact || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Supprimer cette formation ?")) return;
    try {
      await lumi.entities.organisme_formations.delete(id);
      toast.success("Formation supprimée.");
      await fetchMyFormations();
    } catch (err) {
      console.error("[organisme_formations] delete failed", err);
      toast.error("Suppression impossible.");
    }
  };

  const formatDate = (iso: string) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const stats = useMemo(() => ({
    total: items.length,
    upcoming: items.filter((f) => f.sessionDate && new Date(f.sessionDate) >= new Date()).length,
    regions: new Set(items.map((f) => f.region)).size,
    pending: items.filter((f) => (f.validationStatus ?? "pending") === "pending").length,
  }), [items]);

  const displayName = companyProfile?.raisonSociale || profile?.company || profile?.fullName || "Votre centre";

  return (
    <Layout>
      <Seo
        title="Tableau de bord organisme — Emplois BNSSA & MNS"
        description="Gérez vos sessions de formation BNSSA, BPJEPS AAN, BEESAN et recyclages. Espace dédié aux centres et organismes de formation."
      />

      <DashboardThemeWrapper>
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-600 via-orange-600 to-red-600 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full text-xs font-semibold w-fit mb-2">
            <Megaphone className="w-3.5 h-3.5" />
            Espace Organisme de Formation
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 sm:flex-1">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 break-words">{displayName}</h1>
              <p className="text-amber-50 break-words">
                Publiez et gérez vos prochaines sessions de formation.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <Link
                to="/messages"
                className="relative inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                aria-label={unreadMessagesCount > 0 ? `Messagerie (${unreadMessagesCount} non lus)` : "Messagerie"}
              >
                <MessageSquare className="w-4 h-4" />
                Messagerie
                {unreadMessagesCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                    {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
                  </span>
                )}
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Formations soumises", value: stats.total, icon: GraduationCap, color: "bg-amber-100 text-amber-700" },
            { label: "En attente de validation", value: stats.pending, icon: Clock, color: "bg-orange-100 text-orange-700" },
            { label: "Sessions à venir", value: stats.upcoming, icon: Calendar, color: "bg-blue-100 text-blue-700" },
            { label: "Régions couvertes", value: stats.regions, icon: MapPin, color: "bg-green-100 text-green-700" },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-600">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Coordonnées organisme — bloc pliable obligatoire */}
      {/* ── Onglets ──────────────────────────────────────────────────────── */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-10" aria-label="Navigation principale du dashboard organisme">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 overflow-x-auto -mb-px" role="tablist">
            {[
              { id: "formations" as const, label: "Mes formations", icon: BookOpen },
              { id: "compte" as const, label: "Mon compte", icon: UserCircle },
            ].map((t) => {
              const Icon = t.icon;
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleChangeTab(t.id)}
                  className={`relative inline-flex items-center gap-2 whitespace-nowrap px-4 sm:px-5 py-3 text-sm font-semibold transition-colors border-b-2 ${
                    isActive
                      ? "border-amber-600 text-amber-700"
                      : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      </section>

      {activeTab === "formations" && (
        <>
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!isCompanyComplete && (
            <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-0.5">Complétez vos coordonnées d'organisme</p>
                <p className="text-amber-800">
                  La publication de formations est <strong>bloquée</strong> tant que la raison sociale, le SIRET, l'adresse, le téléphone et le nom du responsable ne sont pas renseignés.
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setCoordsOpen((v) => !v)}
            className="w-full flex items-center justify-between bg-white border border-gray-200 rounded-xl px-5 py-3 hover:bg-gray-50 transition-colors mb-3"
            aria-expanded={coordsOpen}
          >
            <span className="flex items-center gap-2 text-sm font-bold text-gray-900">
              <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <GraduationCap className="w-4 h-4" />
              </span>
              Mes coordonnées d'organisme
              {isCompanyComplete ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold">
                  <CheckCircle2 className="w-3 h-3" />
                  Complétées
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold">
                  <AlertCircle className="w-3 h-3" />
                  À compléter
                </span>
              )}
            </span>
            <span className="text-xs text-gray-500 inline-flex items-center gap-1">
              {coordsOpen ? "Replier" : "Dérouler"}
              {coordsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </button>

          {coordsOpen && (
            <CompanyProfileForm
              creator={creator}
              email={email}
              accountType="organisme"
              theme="organisme"
              intro="Ces informations sont obligatoires pour publier vos formations sur bnssa-mns.fr. Elles sont visibles uniquement par notre administration et ne sont jamais affichées sur les pages publiques ni transmises aux candidats."
              onSaved={() => {
                refreshCompany();
                setCoordsOpen(false);
              }}
            />
          )}
        </div>
      </section>

      {/* Main */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Mes formations</h2>
            {!showForm && (
              <button
                onClick={handleStartCreate}
                disabled={!isCompanyComplete}
                title={isCompanyComplete ? "" : "Complétez d'abord vos coordonnées d'organisme"}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isCompanyComplete
                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isCompanyComplete ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                Publier une formation
              </button>
            )}
          </div>

          {/* Form */}
          {showForm && (
            <div className="bg-white border border-amber-200 rounded-xl p-6 mb-8 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-gray-900">
                  {editingId ? "Modifier la formation" : "Nouvelle formation"}
                </h3>
                <button
                  type="button"
                  onClick={resetForm}
                  aria-label="Fermer"
                  className="p-1.5 hover:bg-gray-100 rounded-md"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
                Votre formation sera <strong>soumise à validation</strong> par l'administrateur de bnssa-mns.fr avant d'apparaître sur la page publique <Link to="/formations" className="underline font-semibold">/formations</Link>.
              </div>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="title" className="block text-xs font-semibold text-gray-700 mb-1">
                    Titre de la formation *
                  </label>
                  <input
                    id="title"
                    type="text"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Ex : Formation BNSSA — Session printemps"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="category" className="block text-xs font-semibold text-gray-700 mb-1">
                    Catégorie *
                  </label>
                  <select
                    id="category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value as OrganismeFormationDoc["category"] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-500 outline-none"
                  >
                    <option value="BNSSA">BNSSA</option>
                    <option value="MNS">MNS</option>
                    <option value="Recyclage">Recyclage</option>
                    <option value="Perfectionnement">Perfectionnement</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="region" className="block text-xs font-semibold text-gray-700 mb-1">
                    Région *
                  </label>
                  <select
                    id="region"
                    value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value as Region })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-500 outline-none"
                  >
                    {REGIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="location" className="block text-xs font-semibold text-gray-700 mb-1">
                    Lieu (ville + département) *
                  </label>
                  <input
                    id="location"
                    type="text"
                    required
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Ex : Lyon (69)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="sessionDate" className="block text-xs font-semibold text-gray-700 mb-1">
                    Date de la session *
                  </label>
                  <input
                    id="sessionDate"
                    type="date"
                    required
                    value={form.sessionDate}
                    onChange={(e) => setForm({ ...form, sessionDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="duration" className="block text-xs font-semibold text-gray-700 mb-1">
                    Durée
                  </label>
                  <input
                    id="duration"
                    type="text"
                    value={form.duration}
                    onChange={(e) => setForm({ ...form, duration: e.target.value })}
                    placeholder="Ex : 6 semaines / 180 heures"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-xs font-semibold text-gray-700 mb-1">
                    Description courte
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Présentation générale de la formation, objectifs, public visé…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none resize-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="programme" className="block text-xs font-semibold text-gray-700 mb-1">
                    Programme détaillé
                  </label>
                  <textarea
                    id="programme"
                    rows={5}
                    value={form.programme}
                    onChange={(e) => setForm({ ...form, programme: e.target.value })}
                    placeholder={`Une ligne par module ou journée. Ex :
Jour 1 — Sauvetage aquatique & techniques de remorquage
Jour 2 — Réglementation, surveillance et POSS
Jour 3 — RCP, DAE et premiers secours`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none resize-none"
                  />
                  <p className="mt-1 text-[11px] text-gray-500">Astuce : une ligne par point pour un affichage en liste sur la fiche publique.</p>
                </div>
                <div>
                  <label htmlFor="prerequisites" className="block text-xs font-semibold text-gray-700 mb-1">
                    Prérequis
                  </label>
                  <textarea
                    id="prerequisites"
                    rows={3}
                    value={form.prerequisites}
                    onChange={(e) => setForm({ ...form, prerequisites: e.target.value })}
                    placeholder="Ex : 18 ans minimum, PSE1 à jour, certificat médical, savoir nager 50 m…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none resize-none"
                  />
                </div>
                <div>
                  <label htmlFor="certification" className="block text-xs font-semibold text-gray-700 mb-1">
                    Certification délivrée
                  </label>
                  <input
                    id="certification"
                    type="text"
                    value={form.certification}
                    onChange={(e) => setForm({ ...form, certification: e.target.value })}
                    placeholder="Ex : Diplôme BNSSA, BPJEPS AAN, Attestation de recyclage PSE1…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="contact" className="block text-xs font-semibold text-gray-700 mb-1">
                    Contact pour inscription (téléphone, email ou site)
                  </label>
                  <input
                    id="contact"
                    type="text"
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    placeholder="Ex : 04 78 12 34 56 — contact@centre.fr"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>

                <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {submitting ? "Enregistrement…" : editingId ? "Mettre à jour" : "Soumettre pour validation"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Liste */}
          {loadingList ? (
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-10 text-center text-sm text-gray-500">
              Chargement de vos formations…
            </div>
          ) : items.length === 0 ? (
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-10 text-center">
              <GraduationCap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Aucune formation publiée pour l'instant.</p>
              <p className="text-xs text-gray-500 mt-1">
                {isCompanyComplete
                  ? "Cliquez sur « Publier une formation » pour annoncer votre première session."
                  : "Complétez vos coordonnées d'organisme pour pouvoir publier votre première formation."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((f) => {
                const isPast = f.sessionDate ? new Date(f.sessionDate) < new Date() : false;
                return (
                  <div key={f._id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow overflow-hidden">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                          f.category === "BNSSA" ? "bg-red-100 text-red-700"
                          : f.category === "MNS" ? "bg-blue-100 text-blue-700"
                          : "bg-amber-100 text-amber-700"
                        }`}>
                          {f.category}
                        </span>
                        <StatusPill status={f.validationStatus} />
                        {isPast && (
                          <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Session passée
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(f)}
                          aria-label="Modifier"
                          className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(f._id)}
                          aria-label="Supprimer"
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1.5 leading-snug break-words">{f.title}</h3>
                    <ul className="space-y-1 text-xs text-gray-600 mb-3">
                      <li className="flex items-start gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span className="break-words min-w-0">{formatDate(f.sessionDate)} — {f.duration || "Durée non précisée"}</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span className="break-words min-w-0">{f.location} — {f.region}</span>
                      </li>
                    </ul>
                    {f.description && (
                      <p className="text-xs text-gray-500 line-clamp-2 break-words">{f.description}</p>
                    )}
                    {f.certification && (
                      <p className="mt-1.5 text-[11px] text-emerald-700 font-semibold inline-flex items-start gap-1 break-words">
                        <CheckCircle2 className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="break-words min-w-0">{f.certification}</span>
                      </p>
                    )}
                    {f.validationStatus === "rejected" && f.rejectionReason && (
                      <div className="mt-2 text-[11px] bg-red-50 border border-red-100 text-red-800 rounded px-2 py-1 break-words">
                        <strong>Motif du refus :</strong> {f.rejectionReason}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-5">
            <p className="text-xs text-blue-900 leading-relaxed">
              <strong>ℹ️ Bon à savoir :</strong> Chaque formation soumise est validée par l'administrateur de bnssa-mns.fr avant d'apparaître sur la page publique{" "}
              <Link to="/formations" className="underline font-semibold">/formations</Link>. Vous serez notifié par email à chaque changement de statut.
            </p>
          </div>
        </div>
      </section>
        </>
      )}

      {activeTab === "compte" && (
        <section className="py-8 sm:py-10 bg-gray-50 min-h-[60vh]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AccountSettings email={user?.email || ""} accent="amber" />
          </div>
        </section>
      )}

      </DashboardThemeWrapper>
    </Layout>
  );
}
